import { StandardRequest, StandardResponse } from "./functions";
import { MediaModel } from "./media";
import { ILocation } from "./location";
import { ITagAssociationMeta, TagModel } from "./tag";
import {
  IEntityContactMeta,
  IEntityGroupMeta,
  IEntityMediaMeta,
  IEntityMemoryMeta,
  IEntityPlaceMeta,
  IEntityAnchorMeta,
} from "./entity-links";
import type { ContactModel } from "./contacts";
import type { GroupModel } from "./groups";
import type { MemoryModel } from "./memories";
import type { AnchorModel } from "./anchors";
import { IUnifiedFilter } from "./filters";
import { ShareSummary, SharedToSummary, ISharedEntity } from "./shares";
import { IDate } from "./date";

/**
 * Raw persisted database document schema for a Place entity in Firestore (`places/{id}`).
 */
export interface IPlace {
  /** Unique Firestore document identifier */
  id?: string;
  /** Primary identifier of the user who owns this place (FK -> appusers.userId) */
  ownerId: string;
  /** Canonical headline display name of the place */
  name: string;
  /** Rich-text or markdown narrative description of the place */
  description?: string;
  /** Chronological date or structured date payload */
  date?: string | IDate;
  /** Human-readable street or postal address */
  address?: string;
  /** Normalized geocoded location JSON block (latitude, longitude, text) */
  location?: ILocation;
  /** Primary cover photo media ID foreign key referencing media.id */
  photoMediaId?: string;
  /** Favourite rating from 1 to 5 (default 3) */
  favouriteRating?: number;
  /** Visibility toggle flag */
  isActive?: boolean;

  /** Foreign key relationship map of associated memories Record<memoryId, IEntityMemoryMeta> */
  memories?: Record<string, IEntityMemoryMeta>;
  /** Companion synchronized array of memory IDs maintained for Firestore query indexing */
  memoryIds?: string[];

  /** Foreign key relationship map of associated media Record<mediaId, IEntityMediaMeta> */
  media?: Record<string, IEntityMediaMeta>;
  /** Companion synchronized array of media IDs maintained for Firestore array-contains query indexing */
  mediaIds?: string[];

  /** Foreign key relationship map of associated contacts Record<contactId, IEntityContactMeta> */
  contacts?: Record<string, IEntityContactMeta>;
  /** Companion synchronized array of contact IDs maintained for Firestore query indexing */
  contactIds?: string[];

  /** Foreign key relationship map of associated groups Record<groupId, IEntityGroupMeta> */
  groups?: Record<string, IEntityGroupMeta>;
  /** Companion synchronized array of group IDs maintained for Firestore query indexing */
  groupIds?: string[];

  /** Foreign key relationship map of associated tags Record<tagId, ITagAssociationMeta> */
  tags?: Record<string, ITagAssociationMeta>;
  /** Companion synchronized array of tag IDs maintained for Firestore query indexing */
  tagIds?: string[];
  /** Foreign key relationship map of associated places Record<placeId, IEntityPlaceMeta> */
  places?: Record<string, IEntityPlaceMeta>;
  /** Companion synchronized array of place IDs maintained for Firestore query indexing */
  placeIds?: string[];
  /** Foreign key relationship map of associated anchors Record<anchorId, IEntityAnchorMeta> */
  anchors?: Record<string, IEntityAnchorMeta>;
  /** Companion synchronized array of anchor IDs maintained for Firestore query indexing */
  anchorIds?: string[];

  /** Epoch timestamp in milliseconds when the place was created */
  createdAt: number;
  /** Epoch timestamp in milliseconds of the last modification */
  updatedAt?: number;
}

export type Place = IPlace;

/**
 * Hydrated presentation view model for a Place entity returned over APIs and consumed by frontend clients.
 * Combines persisted document fields with joined foreign entity models, authorization roles, and signed media URLs.
 */
export type PlaceModel = Place & ISharedEntity & {
  /** Canonical hydrated Media view models associated with this place */
  mediaItems?: MediaModel[];
  /** Canonical hydrated Memory view models associated with this place */
  memoryItems?: MemoryModel[];
  /** Canonical hydrated Contact view models associated with this place */
  contactItems?: ContactModel[];
  /** Canonical hydrated Group view models associated with this place */
  groupItems?: GroupModel[];
  /** Canonical hydrated Tag view models associated with this place */
  tagItems?: TagModel[];
  /** Canonical hydrated related Place view models associated with this place */
  placeItems?: PlaceModel[];
  /** Canonical hydrated Anchor view models associated with this place */
  anchorItems?: AnchorModel[];

  /** Signed tokenized thumbnail URL for the place cover photo */
  photoUrl?: string;
  /** Signed tokenized icon URL for the place cover photo */
  photoIconUrl?: string;

  /** Contextual authorization level for the calling user */
  userRole?: "owner" | "editor" | "viewer";
  /** Whether the calling user has permission to share this place */
  canShare?: boolean;
  /** Recipients this place is shared with */
  sharedWith?: ShareSummary[];
  /** Sources this place is shared to */
  sharedTo?: SharedToSummary[];
  /** Profile display name of the user who owns this place */
  ownerDisplayName?: string;
};

/**
 * Request payload to create a new place.
 */
export type CreatePlaceRequest = StandardRequest & {
  name: string;
  description?: string;
  date?: string | IDate;
  address?: string;
  location?: ILocation;
  photoMediaId?: string;
  favouriteRating?: number;
  memories?: Record<string, IEntityMemoryMeta>;
  memoryIds?: string[];
  media?: Record<string, IEntityMediaMeta>;
  mediaIds?: string[];
  contacts?: Record<string, IEntityContactMeta>;
  contactIds?: string[];
  groups?: Record<string, IEntityGroupMeta>;
  groupIds?: string[];
  tags?: Record<string, ITagAssociationMeta>;
  tagIds?: string[];
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
};

/**
 * Response payload after creating a place.
 */
export type CreatePlaceResponse = StandardResponse & {
  place?: PlaceModel;
  id?: string;
};

/**
 * Request payload to edit an existing place.
 */
export type EditPlaceRequest = StandardRequest & {
  id: string;
  name?: string;
  description?: string;
  date?: string | IDate;
  address?: string;
  location?: ILocation;
  photoMediaId?: string;
  favouriteRating?: number;
  isActive?: boolean;
  memories?: Record<string, IEntityMemoryMeta>;
  memoryIds?: string[];
  media?: Record<string, IEntityMediaMeta>;
  mediaIds?: string[];
  contacts?: Record<string, IEntityContactMeta>;
  contactIds?: string[];
  groups?: Record<string, IEntityGroupMeta>;
  groupIds?: string[];
  tags?: Record<string, ITagAssociationMeta>;
  tagIds?: string[];
  places?: Record<string, IEntityPlaceMeta>;
  placeIds?: string[];
  anchors?: Record<string, IEntityAnchorMeta>;
  anchorIds?: string[];
};

/**
 * Response payload after editing a place.
 */
export type EditPlaceResponse = StandardResponse & {
  place?: PlaceModel;
};

/**
 * Request payload to delete a place.
 */
export type DeletePlaceRequest = StandardRequest & {
  id: string;
};

/**
 * Response payload after deleting a place.
 */
export type DeletePlaceResponse = StandardResponse & {
  deleted?: boolean;
};

/**
 * Request payload to get a single place by ID.
 */
export type GetPlaceRequest = StandardRequest & {
  id: string;
};

/**
 * Response payload when fetching a single place.
 */
export type GetPlaceResponse = StandardResponse & {
  place?: PlaceModel;
};

/**
 * Filter criteria for simple place queries.
 */
export type PlaceFilter = {
  name?: string;
};

/**
 * Request payload to get places owned by or shared with the caller.
 */
export type GetMyPlacesRequest = StandardRequest & {
  filter?: PlaceFilter | IUnifiedFilter;
};

/**
 * Response payload returning places owned by or shared with the caller.
 */
export type GetMyPlacesResponse = StandardResponse & {
  places: PlaceModel[];
};

/**
 * Request payload for filtered place search.
 */
export type GetFilteredPlacesRequest = StandardRequest & {
  filter?: IUnifiedFilter;
};

/**
 * Response payload returning filtered places.
 */
export type GetFilteredPlacesResponse = StandardResponse & {
  places: PlaceModel[];
};

/**
 * Request payload to retrieve all platform places (administrator only).
 */
export type GetAllPlacesRequest = StandardRequest & {
  filter?: PlaceFilter | IUnifiedFilter;
};

/**
 * Response payload returning all platform places.
 */
export type GetAllPlacesResponse = StandardResponse & {
  places?: PlaceModel[];
};
